import classNames from "classnames";
import React, {
  Key,
  useRef,
  useCallback,
  ReactNode,
  useEffect,
  useState,
} from "react";
import {
  Table,
  TableProps,
  TableColumnsType,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import { AnimatePresence, motion } from "framer-motion";
import type {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
  ExpandableConfig,
} from "antd/es/table/interface";
import {
  TableData,
  FilterSearchOption,
  VLTableActionsConfig,
} from "./tableUtils";
import useFilteredData from "./useFilteredData";
import useTableHeight from "./useTableHeight";
import { useTablePagination } from "./useTablePagination";
import useScrollStatus from "hooks/useScrollStatus";
import TableAction from "./TableAction/TableAction";
import TableFilters, { TableFiltersProps } from "./TableFilters/TableFilters";
import PaginationInfo from "./PaginationInfo/PaginationInfo";
import styles from "./style.module.scss";
import { LoadingOutlined } from "@ant-design/icons";
import { CaretToggle } from "../CaretToggle/CaretToggle";

export type TableRowSelection<T> = NonNullable<TableProps<T>["rowSelection"]>;

export interface FilterOption {
  text: string;
  value: string;
}

export interface AvailableFilter {
  key: string;
  title: string;
  options: FilterOption[];
  searchable?: boolean;
  type?: "multi" | "single";
}

export interface VLTableProps<T extends TableData = TableData> {
  columns: TableColumnsType<T>;
  dataSource: T[];
  className?: string;
  loading?: boolean;
  showHeader?: boolean;
  selectedRowKeys?: Key[];
  disabledRowKeys?: Key[];
  showSelectAll?: boolean;
  checkboxDisabledKeys?: Key[];
  checkboxTooltipData?: Record<string, string>;
  filterSearchOption?: FilterSearchOption;
  actions?: VLTableActionsConfig<T>;
  tableFilters?: TableFiltersProps;
  onSelectAll?: () => void;
  onSelectionChange?: (selectedRowKeys: Key[], selectedRows: T[]) => void;
  onTableFilterChange?: (filters: Record<string, string[] | null>) => void;
  onSortChange?: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[],
    extra: TableCurrentDataSource<T>,
  ) => void;
  size?: "small" | "middle";
  rowKey: string | ((record: T) => string);
  bottomMessage?: string;
  expandable?: ExpandableConfig<T>;
  onRow?: (record: T, index?: number) => React.HTMLAttributes<HTMLElement>;
  pagination?: Pagination;
  localeEmptyText?: string;
  rowClassName?: (item: T) => string;
}

export interface Pagination {
  total: number;
  current?: number;
  tooltip?: string;
  desktopItemsLength?: number;
  laptopItemsLength?: number;
}

function VLTable<T extends TableData = TableData>({
  columns,
  dataSource,
  // optional
  className,
  selectedRowKeys,
  filterSearchOption,
  disabledRowKeys,
  checkboxDisabledKeys,
  checkboxTooltipData,
  tableFilters,
  onTableFilterChange,
  onSortChange,
  actions,
  loading = false,
  showHeader = true,
  showSelectAll = false,
  onSelectAll,
  onSelectionChange,
  size = "small",
  rowKey,
  bottomMessage,
  expandable,
  onRow,
  pagination,
  localeEmptyText,
  rowClassName,
}: VLTableProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [tableBodyElement, setTableBodyElement] =
    useState<HTMLDivElement | null>(null);
  const filteredDataSource = useFilteredData<T>(dataSource, filterSearchOption);
  const isSelectionEnabled =
    Array.isArray(selectedRowKeys) && onSelectionChange;

  const { page, pageSize, navigateToPage } = useTablePagination(pagination);
  // Find the actual scrollable body element within the Ant Design table
  useEffect(() => {
    const antTableBody = containerRef.current?.querySelector(
      ".ant-table-body",
    ) as HTMLDivElement;
    if (antTableBody) {
      setTableBodyElement(antTableBody);
    }
  }, [filteredDataSource.length, columns.length]); // Re-run if table content or columns change

  // Track scrollable states for fade effects and bottom detection
  const scrollableStatus = useScrollStatus({ current: tableBodyElement });
  const { y: isScrollableY, isScrolledToBottom } = scrollableStatus;

  const tableClassNames = classNames(styles.vlTable, className, {
    [styles.scrollableY]: isScrollableY,
  });

  // Handle table filter changes (capture when user changes column filters)
  const handleTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<T> | SorterResult<T>[],
      extra: TableCurrentDataSource<T>,
    ) => {
      // Skip when pagination triggers the change
      if (extra.action === "paginate") {
        return;
      }

      if (onTableFilterChange) {
        // Convert Ant Design filters to our format
        const convertedFilters: Record<string, string[] | null> = {};
        Object.entries(filters).forEach(([key, values]) => {
          convertedFilters[key] =
            Array.isArray(values) && values.length > 0
              ? (values as string[])
              : null;
        });
        onTableFilterChange(convertedFilters);
      }
      // Also call the onSortChange handler if provided
      if (onSortChange) {
        onSortChange(pagination, filters, sorter, extra);
      }
    },
    [onTableFilterChange, onSortChange],
  );

  const tableHeight = useTableHeight(containerRef, showHeader, !!tableFilters);

  // Enhanced row selection with disabled row support
  const rowSelection = isSelectionEnabled
    ? {
        selectedRowKeys,
        onChange: onSelectionChange,
        hideSelectAll: !showSelectAll, // Show/hide the Select All checkbox based on showSelectAll prop
        onSelectAll:
          showSelectAll && onSelectAll
            ? (selected: boolean) => {
                if (selected && onSelectAll) {
                  onSelectAll();
                }
              }
            : undefined,
        renderCell: (
          checked: boolean,
          record: T,
          index: number,
          originNode: ReactNode,
        ) => {
          const tooltipMessage = checkboxTooltipData?.[record.key as string];

          if (tooltipMessage) {
            return (
              <Tooltip
                data-testid="vl-table-checkbox-tooltip"
                title={tooltipMessage}
                placement="top"
              >
                {originNode}
              </Tooltip>
            );
          }

          return originNode;
        },
        getCheckboxProps: (record: T) => ({
          disabled:
            checkboxDisabledKeys?.includes(record.key as Key) ||
            disabledRowKeys?.includes(record.key as Key),
        }),
      }
    : undefined;

  // Custom row className to handle disabled styling
  const getRowClassnames = (record: T) => {
    return classNames(
      {
        [styles.disabledRow]: disabledRowKeys?.includes(record.key as Key),
        [styles.checkboxDisabledRow]: checkboxDisabledKeys?.includes(
          record.key as Key,
        ),
      },
      rowClassName?.(record),
    );
  };

  // Build actions column if actions are provided
  const tableColumns: TableColumnsType<T> = actions
    ? [
        ...columns,
        {
          key: "vl-table-actions",
          width: "64px",
          render: (_, record) => {
            return <TableAction menuItems={actions.items(record)} />;
          },
        },
      ]
    : columns;

  return (
    <div ref={containerRef} className={styles.tableContainer}>
      {tableFilters && (
        <TableFilters
          availableFilters={tableFilters.availableFilters}
          filters={tableFilters.filters}
          onTableFilterChange={tableFilters.onTableFilterChange}
        />
      )}
      <Table<T>
        size={size}
        columns={tableColumns}
        loading={
          loading
            ? {
                spinning: true,
                indicator: <LoadingOutlined />,
              }
            : false
        }
        data-testid="vl-table"
        showHeader={showHeader}
        className={tableClassNames}
        rowSelection={rowSelection}
        dataSource={filteredDataSource}
        rowClassName={getRowClassnames}
        scroll={pagination ? undefined : { y: tableHeight }}
        locale={localeEmptyText ? { emptyText: localeEmptyText } : undefined}
        pagination={
          pagination
            ? {
                position: ["topRight"],
                showSizeChanger: false,
                total: pagination?.total,
                pageSize: pageSize,
                current: page, // Use local page state that syncs with URL
                onChange: (newPage) => {
                  navigateToPage(newPage);
                },
                showTotal: (total, range) => (
                  <PaginationInfo
                    range={range}
                    total={total}
                    tooltip={pagination?.tooltip}
                  />
                ),
                style: { margin: 0, marginBottom: 16 },
              }
            : false
        }
        onChange={handleTableChange}
        rowKey={rowKey}
        expandable={{
          ...expandable,
          expandIcon: expandable
            ? ({ expanded, onExpand, record }) => (
                <CaretToggle
                  expanded={expanded}
                  onClick={(e: React.MouseEvent<HTMLElement>) =>
                    onExpand(record, e)
                  }
                />
              )
            : undefined,
        }}
        onRow={onRow}
      />
      <AnimatePresence>
        {bottomMessage && isScrolledToBottom && (
          <motion.div
            key="bottom-message"
            initial={{ opacity: 0, y: 10, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: 10, x: "-50%" }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className={styles.bottomMessage}
          >
            {bottomMessage}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default VLTable;
